import React, { useEffect, useState } from "react";
import { Card, Button, Drawer, Form, Input, Upload, message, Tabs, Table } from "antd";
import { FacebookOutlined, TwitterOutlined, LinkedinOutlined, GlobalOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchCompanyDetails, updateCompanyDetails, createCompanyDetails, fetchCompanyDetailsById } from "../../../core/data/redux/company/companyDetailsActions"; // 1. Import createCompanyDetails

const CompanyProfile = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { selectedCompany = {} } = useSelector((state: any) => state.companyDetails || {});
    const { user } = useSelector((state: any) => state.auth);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const { createLoading } = useSelector((state: any) => state.companyDetails);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [mode, setMode] = useState<'edit' | 'create'>('edit');
    const [companyImage, setCompanyImage] = useState<any>(null);
    const [pluginLogo, setPluginLogo] = useState<any>(null);
    const [subscriptionData, setSubscriptionData] = useState<any>(null);
    const { activeCompany } = useSelector((state: any) => state.companyDetails);
    const { TabPane } = Tabs;


    // Define a blank state for the form for reuse
    const initialFormState = {
        company_detail_id: null,
        UserId: user?.UserId,
        company_image: '',
        plugin_logo: '',
        domain_name: '',
        domain_enc_url: '',
        company_name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        phone: '',
        authorize_name: '',
        authorize_designation: '',
        authorize_email: '',
        authorize_sign: '',
        authorize_mobile: '',
        currency: '',
        fax: '',
        website: '',
        facebook: '',
        twitter: '',
        linkedin: '',
        financial: '0',
        gst: ''
    };
    const [companyForm, setCompanyForm] = useState(initialFormState);

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!companyForm.company_name || companyForm.company_name.trim() === '') {
            newErrors.company_name = "Company name is required";
        }
        if (!companyForm.authorize_email || !/\S+@\S+\.\S+/.test(companyForm.authorize_email)) {
            newErrors.authorize_email = "Invalid email address";
        }

        if (!companyForm.phone || !/^\+?\d{7,15}$/.test(companyForm.phone)) {
            newErrors.phone = "Invalid phone number";
        }

        if (!companyForm.gst || companyForm.gst.length < 5) {
            newErrors.gst = "GST number too short";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };



    const fetchSubscriptionDetails = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE_SERVER_BASE_URL}/auth/subscription-detail`, {
                method: "GET",
                credentials: "include",
            });
            const result = await response.json();
            if (result.status === "success") {
                setSubscriptionData(result.data);
            } else {
                message.error("Failed to fetch subscription details");
            }
        } catch (error) {
            console.error("Error fetching subscription details:", error);
            message.error("Error loading subscription details");
        }
    };

    const handleImageChange = (file: any, type: "company" | "plugin") => {
        const previewURL = URL.createObjectURL(file);
        if (type === "company") {
            setCompanyImage(previewURL);
            setCompanyForm((prev) => ({ ...prev, company_image: file }));
        } else {
            setPluginLogo(previewURL);
            setCompanyForm((prev) => ({ ...prev, plugin_logo: file }));
        }
        return false;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCompanyForm((prevForm) => ({
            ...prevForm,
            [name]: value,
        }));
    };

    useEffect(() => {
        fetchSubscriptionDetails();
    }, [dispatch]);



    useEffect(() => {
        if (selectedCompany?.company_detail_id) {
            setCompanyForm({
                ...initialFormState,
                ...selectedCompany
            });
        }
    }, [selectedCompany]);


    const showDrawer = (viewMode: 'create' | 'edit') => {
        setMode(viewMode);
        if (viewMode === 'create') {
            // Reset form for creating a new company
            setCompanyForm(initialFormState);
            setCompanyImage(null);
            setPluginLogo(null);
        } else {
            // Populate form with existing details for editing
            setCompanyForm({ ...initialFormState, ...selectedCompany });
            setCompanyImage(null); // Reset local image previews
            setPluginLogo(null);
        }
        setDrawerVisible(true);
    };

    const closeDrawer = () => {
        setDrawerVisible(false);
        setCompanyImage(null);
        setPluginLogo(null);
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }
        const formData = new FormData();

        Object.entries(companyForm).forEach(([key, value]) => {
            if (key !== "created_at" && key !== "updated_at" && value !== null && value !== undefined) {
                formData.append(key, value);
            }
        });

        // 2. Dynamically call create or update thunk based on the mode
        if (mode === 'create') {
            dispatch(createCompanyDetails(formData))
                .unwrap()
                .then(() => {
                    message.success('Company created successfully');
                    dispatch(fetchCompanyDetailsById(Number(activeCompany?.company_detail_id)));
                    dispatch(fetchCompanyDetails())
                    closeDrawer();
                })
                .catch((err) => {
                    const errorMessage = err.message || 'Failed to create company';
                    message.error(`Creation failed: ${errorMessage}`);
                });
        } else {
            // 3. Correctly structure the payload for the update action
            const company_detail_id = companyForm.company_detail_id;
            if (!company_detail_id) {
                message.error("Company ID is missing, cannot update.");
                return;
            }
            dispatch(updateCompanyDetails({ company_detail_id, formData }))
                .unwrap()
                .then(() => {
                    message.success('Update successful');
                    dispatch(fetchCompanyDetailsById(Number(activeCompany?.company_detail_id)));
                    closeDrawer();
                })
                .catch((err) => {
                    const errorMessage = err.message || 'Failed to update company';
                    message.error(`Update failed: ${errorMessage}`);
                });
        }
    };

    if (!selectedCompany) {
        return <div>Loading company details...</div>;
    }

    const data = [
        { key: 1, label: "Company Name", value: selectedCompany.company_name },
        { key: 2, label: "Phone", value: selectedCompany.phone },
        { key: 3, label: "Email", value: selectedCompany.authorize_email },
        { key: 4, label: "Address", value: selectedCompany.address },
        { key: 5, label: "GST", value: selectedCompany.gst },
        { key: 6, label: "Currency", value: selectedCompany.currency },
        { key: 7, label: "Authorized Name", value: selectedCompany.authorize_name },
        { key: 8, label: "Designation", value: selectedCompany.authorize_designation },
        { key: 9, label: "Mobile", value: selectedCompany.authorize_mobile },
    ];


    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="container mt-4">
                    <Card className="shadow-lg p-4 position-relative" style={{ borderRadius: "12px" }}>
                        <div className="d-flex justify-content-between mb-4">
                            <img
                                src={selectedCompany.plugin_logo}
                                alt="Logo"
                                className="px-5 bg-secondary rounded-pill"
                                style={{ width: '300px', height: 'auto', objectFit: 'contain' }}
                            />
                            <div>
                                <h2 className="mt-3">{selectedCompany.company_name}</h2>
                                <p className="text-muted">{selectedCompany.domain_name}</p>
                            </div>
                        </div>

                        {(user?.role_name === "Admin" || user?.UserId === "Admin") && (
                            <div style={{
                                position: 'absolute', top: 10, right: 6,
                                backgroundColor: '#f0f0f0', padding: '6px 10px',
                                borderRadius: '6px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                display: 'flex', gap: '8px'
                            }}>
                                <Button onClick={() => showDrawer('create')}>
                                    Create Company
                                </Button>
                                <Button onClick={() => showDrawer('edit')}>
                                    <i className="ti ti-edit" />
                                </Button>
                            </div>
                        )}

                        <Tabs defaultActiveKey="1">
                            <TabPane tab="Basic Details" key="1">
                                <div className="p-3">
                                    <h5 className="mb-3">Basic Company Info</h5>
                                    <Table
                                        pagination={false}
                                        bordered
                                        columns={[{ title: "Field", dataIndex: "label", key: "label" }, { title: "Value", dataIndex: "value", key: "value" }]}
                                        dataSource={data}
                                    />
                                    <div className="text-center mt-4">
                                        <h5 className="mb-2">Social Media</h5>
                                        <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="me-3"><GlobalOutlined style={{ fontSize: "24px" }} /></a>
                                        <a href={selectedCompany.facebook} target="_blank" rel="noopener noreferrer" className="me-3"><FacebookOutlined style={{ fontSize: "24px", color: "#1877F2" }} /></a>
                                        <a href={selectedCompany.twitter} target="_blank" rel="noopener noreferrer" className="me-3"><TwitterOutlined style={{ fontSize: "24px", color: "#1DA1F2" }} /></a>
                                        <a href={selectedCompany.linkedin} target="_blank" rel="noopener noreferrer"><LinkedinOutlined style={{ fontSize: "24px", color: "#0077B5" }} /></a>
                                    </div>
                                </div>
                            </TabPane>

                            <TabPane tab="Subscription Details" key="2">
                                <div className="p-3">
                                    {subscriptionData ? (
                                        <Table
                                            pagination={false} bordered
                                            columns={[{ title: "Field", dataIndex: "label", key: "label" }, { title: "Value", dataIndex: "value", key: "value" }]}
                                            dataSource={[
                                                { key: 1, label: "Subscription Id", value: <span className="text-warning">{subscriptionData.subscription_key}</span> },
                                                { key: 2, label: "Plan", value: <span className="text-danger">{subscriptionData.plan_name}</span> },
                                                { key: 3, label: "Start Date", value: subscriptionData.start_date },
                                                { key: 4, label: "Expiry Date", value: subscriptionData.end_date },
                                                { key: 5, label: "Users Allowed", value: subscriptionData.total_user },
                                                { key: 6, label: "Subscription Period (days)", value: subscriptionData.subscription_period },
                                                { key: 7, label: "Remaining Days", value: subscriptionData.remaining_days },
                                                { key: 8, label: "Status", value: new Date(subscriptionData.end_date) >= new Date() ? "Active" : "Expired" },
                                            ]}
                                        />
                                    ) : <p>Loading subscription details...</p>}
                                </div>
                            </TabPane>
                        </Tabs>
                    </Card>
                </div>
            </div>

            <Drawer
                title={mode === 'edit' ? "Edit Company Details" : "Create New Company"}
                placement="right" width={600} style={{ marginTop: 50 }}
                onClose={closeDrawer} open={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Form layout="vertical">
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item label="Company Image">
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleImageChange(file, "company")}>
                                    <Button type="primary">{companyImage ? "Change Image" : "Select Image"}</Button>
                                </Upload>
                                <div>
                                    <img
                                        src={companyImage || companyForm.company_image}
                                        alt="Company"
                                        style={{ width: 150, height: 150, marginTop: 10, objectFit: "contain" }}
                                    />
                                </div>
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item label="Plugin Logo">
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleImageChange(file, "plugin")}>
                                    <Button type="primary">{pluginLogo ? "Change Logo" : "Select Logo"}</Button>
                                </Upload>
                                <div>
                                    <img
                                        src={pluginLogo || companyForm.plugin_logo}
                                        alt="Plugin Logo"
                                        style={{ width: 150, height: 150, marginTop: 10, objectFit: "contain" }}
                                    />
                                </div>
                            </Form.Item>
                        </div>
                    </div>
                    
                    <Form.Item label="Company Name" validateStatus={errors.company_name ? "error" : ""} help={errors.company_name}>
                        <Input name="company_name" value={companyForm.company_name} onChange={handleInputChange} />
                    </Form.Item>

                    <Form.Item label="Domain Name"><Input name="domain_name" value={companyForm.domain_name} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Phone" validateStatus={errors.phone ? "error" : ""} help={errors.phone}>
                        <Input name="phone" value={companyForm.phone} onChange={handleInputChange} />
                    </Form.Item>
                    <Form.Item label="Email" validateStatus={errors.authorize_email ? "error" : ""} help={errors.authorize_email}>
                        <Input name="authorize_email" value={companyForm.authorize_email} onChange={handleInputChange} />
                    </Form.Item>
                    <Form.Item label="Address"><Input name="address" value={companyForm.address} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="GST" validateStatus={errors.gst ? "error" : ""} help={errors.gst}>
                        <Input name="gst" value={companyForm.gst} onChange={handleInputChange} />
                    </Form.Item>
                    <Form.Item label="Currency"><Input name="currency" value={companyForm.currency} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Authorized Person"><Input name="authorize_name" value={companyForm.authorize_name} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Mobile"><Input name="authorize_mobile" value={companyForm.authorize_mobile} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Designation"><Input name="authorize_designation" value={companyForm.authorize_designation} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Website"><Input name="website" value={companyForm.website} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Facebook"><Input name="facebook" value={companyForm.facebook} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="Twitter"><Input name="twitter" value={companyForm.twitter} onChange={handleInputChange} /></Form.Item>
                    <Form.Item label="LinkedIn"><Input name="linkedin" value={companyForm.linkedin} onChange={handleInputChange} /></Form.Item>
                    <div className="text-center mt-4">
                        <Button type="primary" onClick={handleSubmit} disabled={createLoading}>
                            {createLoading && <span className="spinner-border spinner-border-sm me-2" />}
                            {mode === 'edit' ? 'Save Changes' : 'Create Company'}
                        </Button>
                    </div>
                </Form>
            </Drawer>
        </div>
    );
};

export default CompanyProfile;